import React from "react";
import { useTranslation } from "react-i18next";
import { useRouter } from "lib/hooks/useRouter";
import { AccountLayout } from "components/AccountLayout/AccountLayout";
import { SEO } from "components/Seo/Seo";
import { getCategoryFromItems } from "lib/utils";
import { JOB_STATUS_MAPPING } from "lib/constants/jobStatusMapping";
import { ApiResponseData, instance, useCatalogue } from "@clearabee/ui-sdk";
import { useQuery } from "react-query";
import { Platform } from "react-native";
import { JobView } from "components/JobView/JobView";
import { PageSpinner } from "components/PageSpinner/PageSpinner";
import { User } from "lib/auth";

interface JobProps {
  jobRef: string;
  job?: ApiResponseData<typeof instance.jobs.getJob>;
  user: User;
}

export const Job = ({
  job: initialJobData,
  jobRef,
  user,
}: JobProps): React.ReactElement => {
  const { navigate } = useRouter();
  const [translate] = useTranslation();
  const { defaultCatalogue } = useCatalogue();
  const { data, isLoading, isError } = useQuery(
    ["getJob", jobRef],
    async () =>
      (await instance.jobs.getJobByRef(encodeURIComponent(jobRef))).data,
    {
      initialData: initialJobData,
      enabled: !initialJobData && !!user,
      onError: () => navigate({ routeName: "JobHistory" }),
    },
  );

  const showTrain = Object.entries(JOB_STATUS_MAPPING).some(
    ([key]) => key === data?.status && "Failed" !== data?.status,
  );

  if (isError) {
    return <PageSpinner />;
  }

  const formattedAddress = [
    data?.addressLine1,
    data?.addressLine2,
    data?.addressCity,
    data?.addressCounty,
    data?.addressPostcode,
  ]
    .filter(Boolean)
    .join("\n");
  const categorySku = getCategoryFromItems(
    defaultCatalogue,
    data?.items ?? [],
  )?.sku;

  return (
    <>
      <SEO title={translate("hwa.screens.job.title", { ref: jobRef })} />

      <AccountLayout
        title={translate("hwa.screens.job.title", {
          ref: Platform.OS === "web" ? jobRef : undefined,
        })}
        subtitle={jobRef}
        backTo={{ routeName: "JobHistory" }}
        tab="JobHistory"
        user={user}
      >
        <JobView
          job={data}
          isLoading={isLoading}
          address={formattedAddress}
          categorySku={categorySku}
          showTrain={showTrain}
        />
      </AccountLayout>
    </>
  );
};
